import React, { useEffect, useState } from "react";
import {
  LayoutDashboard,
  Users,
  Ticket,
  Activity,
  ShieldAlert,
  LogOut,
  Sparkles,
  TrendingUp,
  UserCheck,
  Crown,
  Copy,
  Loader2,
  Search,
  LifeBuoy,
  Mail,
  Compass,
  Download,
} from "lucide-react";
import { API } from "@/lib/constants";

// ============ API helpers ============
const authFetch = async (path, opts = {}) => {
  const r = await fetch(`${API}${path}`, {
    credentials: "include",
    ...opts,
    headers: { "Content-Type": "application/json", ...(opts.headers || {}) },
  });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`);
  return r.json();
};

// ============ Tabs ============
const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "users", label: "Users", icon: Users },
  { key: "promos", label: "Promo codes", icon: Ticket },
  { key: "leads", label: "Guide leads", icon: Compass },
  { key: "support", label: "Support tickets", icon: LifeBuoy },
  { key: "activity", label: "Activity", icon: Activity },
  { key: "security", label: "Security", icon: ShieldAlert },
];

// ============ Sub-components ============

function StatCard({ icon: Icon, label, value, tint = "#D95D39", sub = "" }) {
  return (
    <div className="bg-white border border-[#EAE5DB] rounded-2xl p-5" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="overline">{label}</div>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${tint}15` }}>
          <Icon className="w-4 h-4" style={{ color: tint }} />
        </div>
      </div>
      <div className="font-heading font-extrabold text-3xl text-[#292524]">{value}</div>
      {sub && <div className="text-xs text-[#78716C] mt-1">{sub}</div>}
    </div>
  );
}

function OverviewTab() {
  const [data, setData] = useState(null);
  useEffect(() => { authFetch("/admin/overview").then(setData).catch(() => {}); }, []);
  if (!data) return <div className="text-[#78716C] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard icon={Users} label="Total users" value={data.total_users} tint="#D95D39" sub={`+${data.new_users_month} last 30d`} />
        <StatCard icon={UserCheck} label="Active (24h)" value={data.active_users_day} tint="#2A5C5A" />
        <StatCard icon={Crown} label="Premium" value={data.premium_users} tint="#E5A93D" />
        <StatCard icon={Sparkles} label="CVs generated" value={data.total_optimizations} tint="#3B8268" />
      </div>
      <div className="grid grid-cols-2 gap-4 mb-6">
        <StatCard icon={Ticket} label="Promo codes" value={data.promo_codes_count} tint="#3D7BB5" sub={`${data.promo_redemptions} redemptions`} />
        <StatCard icon={TrendingUp} label="Signups this month" value={data.new_users_month} tint="#9B5DE5" />
      </div>
      {data.signups_chart?.length > 0 && (
        <div className="bg-white border border-[#EAE5DB] rounded-2xl p-5">
          <div className="font-heading font-bold text-[#292524] mb-3">Signups per day (30d)</div>
          <div className="flex items-end gap-1 h-32">
            {data.signups_chart.map((d) => {
              const max = Math.max(...data.signups_chart.map((x) => x.count), 1);
              return (
                <div key={d.date} className="flex-1 flex flex-col items-center justify-end group">
                  <div
                    className="w-full bg-[#D95D39] rounded-t hover:bg-[#C24F2E] transition-colors relative"
                    style={{ height: `${(d.count / max) * 100}%` }}
                    title={`${d.date}: ${d.count}`}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function UsersTab() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (q) p.set("q", q);
      if (planFilter) p.set("plan", planFilter);
      const r = await authFetch(`/admin/users?${p}`);
      setItems(r.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const grantPremium = async (uid, months) => {
    if (!confirm(months === 0 ? "Grant LIFETIME premium?" : `Grant ${months} months premium?`)) return;
    await authFetch("/admin/users/grant-premium", { method: "POST", body: JSON.stringify({ user_id: uid, months }) });
    load();
  };
  const revokePremium = async (uid) => {
    if (!confirm("Revoke premium?")) return;
    await authFetch("/admin/users/revoke-premium", { method: "POST", body: JSON.stringify({ user_id: uid }) });
    load();
  };

  return (
    <div>
      <div className="flex gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#78716C]" />
          <input
            className="input !pl-10"
            placeholder="Search by email or name…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            data-testid="admin-users-search"
          />
        </div>
        <select className="input !w-auto" value={planFilter} onChange={(e) => setPlanFilter(e.target.value)}>
          <option value="">All plans</option>
          <option value="free">Free</option>
          <option value="premium">Premium</option>
        </select>
        <button className="btn-outline" onClick={load} disabled={loading}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Refresh"}
        </button>
      </div>
      <div className="bg-white border border-[#EAE5DB] rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#FAF6F0] border-b border-[#EAE5DB] text-left">
                <th className="px-4 py-3 font-semibold">User</th>
                <th className="px-4 py-3 font-semibold">Plan</th>
                <th className="px-4 py-3 font-semibold">CVs</th>
                <th className="px-4 py-3 font-semibold">Joined</th>
                <th className="px-4 py-3 font-semibold">Last login</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.user_id} className="border-b border-[#F2EBE1] last:border-0">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {u.picture ? (
                        <img src={u.picture} alt="" className="w-8 h-8 rounded-full" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-[#F2EBE1] flex items-center justify-center text-xs font-bold text-[#78716C]">
                          {(u.name || u.email || "?").charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="font-semibold text-[#292524] truncate">{u.name || "—"}</div>
                        <div className="text-xs text-[#78716C] truncate">{u.email}</div>
                      </div>
                      {u.is_admin && <Crown className="w-4 h-4 text-[#E5A93D]" title="Admin" />}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {u.plan === "premium" ? (
                      <span className="tag tag-brand text-[10px]">PREMIUM</span>
                    ) : (
                      <span className="text-xs text-[#78716C]">Free</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[#292524] font-semibold">{u.cv_count || 0}</td>
                  <td className="px-4 py-3 text-xs text-[#78716C]">{(u.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3 text-xs text-[#78716C]">{(u.last_login_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1 justify-end">
                      {u.plan === "premium" ? (
                        <button className="text-xs text-[#D9423E] hover:underline" onClick={() => revokePremium(u.user_id)}>Revoke</button>
                      ) : (
                        <>
                          <button className="text-xs text-[#2A5C5A] hover:underline" onClick={() => grantPremium(u.user_id, 1)}>+1mo</button>
                          <button className="text-xs text-[#2A5C5A] hover:underline" onClick={() => grantPremium(u.user_id, 6)}>+6mo</button>
                          <button className="text-xs text-[#D95D39] hover:underline font-semibold" onClick={() => grantPremium(u.user_id, 0)}>Lifetime</button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={6} className="text-center py-10 text-[#78716C]">No users</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function GuideLeadsTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [bySource, setBySource] = useState({});
  const [sourceFilter, setSourceFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const url = "/admin/guide-leads" + (sourceFilter ? `?source=${sourceFilter}` : "");
      const r = await authFetch(url);
      setItems(r.items || []);
      setTotal(r.total || 0);
      setBySource(r.by_source || {});
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [sourceFilter]);

  const exportCsv = async () => {
    try {
      const token = localStorage.getItem("aussiecv_admin_token") || "";
      const r = await fetch(`${API}/admin/guide-leads/export.csv`, { credentials: "include", headers: { "X-Admin-Token": token } });
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "aussiecv_guide_leads.csv";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 500);
    } catch (e) { alert("Export failed: " + e.message); }
  };

  const SOURCE_LABELS = {
    instagram: "Instagram", tiktok: "TikTok", facebook: "Facebook",
    friend: "Friend / WOM", google: "Google", other: "Other",
  };

  return (
    <div className="space-y-6" data-testid="guide-leads-tab">
      {/* Top stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={Compass} label="Total leads" value={total} tint="#D95D39" />
        {Object.entries(bySource).slice(0, 3).map(([k, v]) => (
          <StatCard key={k} icon={Users} label={SOURCE_LABELS[k] || k} value={v} tint="#2A5C5A" />
        ))}
      </div>

      {/* Filters + export */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-[#78716C]">Source:</span>
          <select className="input !py-1 !text-sm !w-auto" value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} data-testid="leads-source-filter">
            <option value="">All</option>
            {Object.entries(SOURCE_LABELS).map(([k, v]) => (<option key={k} value={k}>{v}</option>))}
          </select>
        </div>
        <button onClick={exportCsv} className="btn-outline !py-1.5 text-sm" data-testid="leads-export-csv">
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#EAE5DB] rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#FAF6F0] text-left">
                <th className="px-4 py-3 font-semibold">Date</th>
                <th className="px-4 py-3 font-semibold">Email</th>
                <th className="px-4 py-3 font-semibold">Phone</th>
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">Source</th>
                <th className="px-4 py-3 font-semibold">Lang</th>
                <th className="px-4 py-3 font-semibold text-right"># DL</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center py-10 text-[#78716C]"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-10 text-[#78716C]">No leads yet.</td></tr>
              ) : items.map((l, i) => (
                <tr key={l.email + i} className="border-b border-[#F2EBE1] last:border-0">
                  <td className="px-4 py-3 text-xs text-[#78716C]">{(l.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-4 py-3 text-xs font-mono">{l.email}</td>
                  <td className="px-4 py-3 text-xs font-mono">{l.phone}</td>
                  <td className="px-4 py-3 text-xs">{l.name || "—"}</td>
                  <td className="px-4 py-3 text-xs">
                    <span className="inline-block px-1.5 py-0.5 rounded bg-[#F2EBE1] text-[10px] font-semibold">
                      {SOURCE_LABELS[l.source] || l.source || "—"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs uppercase">{l.lang || "en"}</td>
                  <td className="px-4 py-3 text-xs text-right font-bold">{l.download_count || 1}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function PromosTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({
    code: "",
    kind: "free_cvs",
    duration_months: 0,
    cv_credits_grant: 3,
    percent_off: 20,
    amount_off: 200,
    currency: "aud",
    max_uses: 10,
    expires_at: "",
    note: "",
  });
  const [redemptions, setRedemptions] = useState({});
  const [expandedCode, setExpandedCode] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    const r = await authFetch("/admin/promo-codes");
    setItems(r.items || []);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await authFetch("/admin/promo-codes", { method: "POST", body: JSON.stringify(form) });
      setForm({ code: "", kind: "free_cvs", duration_months: 0, cv_credits_grant: 3, percent_off: 20, amount_off: 200, currency: "aud", max_uses: 10, expires_at: "", note: "" });
      load();
    } catch (err) {
      alert(err.message);
    } finally { setCreating(false); }
  };

  const toggle = async (code) => {
    await authFetch("/admin/promo-codes/toggle", { method: "POST", body: JSON.stringify({ code }) });
    load();
  };

  const showRedemptions = async (code) => {
    if (expandedCode === code) { setExpandedCode(null); return; }
    const r = await authFetch(`/admin/promo-codes/redemptions?code=${encodeURIComponent(code)}`);
    setRedemptions((cur) => ({ ...cur, [code]: r.items || [] }));
    setExpandedCode(code);
  };

  const generateCode = () => {
    const chars = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
    let c = "";
    for (let i = 0; i < 8; i++) c += chars[Math.floor(Math.random() * chars.length)];
    setForm({ ...form, code: c });
  };

  const describePromo = (p) => {
    const k = p.kind || "free_cvs";
    if (k === "stripe_percent_off") return `-${p.percent_off}% (Stripe)`;
    if (k === "stripe_amount_off") return `-$${((p.amount_off || 0) / 100).toFixed(2)} ${(p.currency || "aud").toUpperCase()} (Stripe)`;
    if (p.cv_credits_grant && p.cv_credits_grant > 0) return `+${p.cv_credits_grant} CV credits`;
    if (p.duration_months === 0) return "Premium Lifetime";
    return `Premium ${p.duration_months} mo`;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-6">
      {/* Create form */}
      <div className="bg-white border border-[#EAE5DB] rounded-2xl p-5">
        <div className="font-heading font-bold text-lg text-[#292524] mb-4">Create promo code</div>
        <form onSubmit={create} className="space-y-3">
          <div>
            <label className="block mb-1 text-xs font-semibold">Code</label>
            <div className="flex gap-2">
              <input className="input flex-1 uppercase" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="LAUNCH50" required data-testid="promo-code-input" />
              <button type="button" onClick={generateCode} className="btn-outline !py-2 !px-3" data-testid="promo-code-random">🎲</button>
            </div>
          </div>

          <div>
            <label className="block mb-1 text-xs font-semibold">Type</label>
            <select
              className="input"
              value={form.kind}
              onChange={(e) => setForm({ ...form, kind: e.target.value })}
              data-testid="promo-kind-select"
            >
              <option value="free_cvs">🎁 Free CVs / Premium (in-app redemption)</option>
              <option value="stripe_percent_off">💯 Stripe — % off (checkout)</option>
              <option value="stripe_amount_off">💵 Stripe — Fixed amount off (checkout)</option>
            </select>
            <div className="text-[11px] text-[#78716C] mt-1">
              {form.kind === "free_cvs" && "Users enter this code in-app to unlock free credits or premium."}
              {form.kind === "stripe_percent_off" && "Applied on the Stripe checkout page. Reduces the price by X%."}
              {form.kind === "stripe_amount_off" && "Applied on the Stripe checkout page. Reduces the price by a fixed amount."}
            </div>
          </div>

          {form.kind === "free_cvs" && (
            <>
              <div>
                <label className="block mb-1 text-xs font-semibold">CV credits to grant</label>
                <input
                  type="number" min="0" className="input"
                  value={form.cv_credits_grant}
                  onChange={(e) => setForm({ ...form, cv_credits_grant: parseInt(e.target.value, 10) || 0 })}
                  data-testid="promo-cv-credits-input"
                />
                <div className="text-[11px] text-[#78716C] mt-1">0 = grant Premium instead (uses duration below)</div>
              </div>
              {(!form.cv_credits_grant || form.cv_credits_grant <= 0) && (
                <div>
                  <label className="block mb-1 text-xs font-semibold">Premium duration</label>
                  <select className="input" value={form.duration_months} onChange={(e) => setForm({ ...form, duration_months: parseInt(e.target.value, 10) })}>
                    <option value="0">Lifetime</option>
                    <option value="1">1 month</option>
                    <option value="3">3 months</option>
                    <option value="6">6 months</option>
                    <option value="12">12 months</option>
                  </select>
                </div>
              )}
            </>
          )}

          {form.kind === "stripe_percent_off" && (
            <div>
              <label className="block mb-1 text-xs font-semibold">Percent off (1–100)</label>
              <input
                type="number" min="1" max="100" className="input"
                value={form.percent_off}
                onChange={(e) => setForm({ ...form, percent_off: parseInt(e.target.value, 10) || 0 })}
                data-testid="promo-percent-input"
              />
            </div>
          )}

          {form.kind === "stripe_amount_off" && (
            <>
              <div>
                <label className="block mb-1 text-xs font-semibold">Amount off (cents)</label>
                <input
                  type="number" min="1" className="input"
                  value={form.amount_off}
                  onChange={(e) => setForm({ ...form, amount_off: parseInt(e.target.value, 10) || 0 })}
                  data-testid="promo-amount-input"
                />
                <div className="text-[11px] text-[#78716C] mt-1">200 = $2.00</div>
              </div>
              <div>
                <label className="block mb-1 text-xs font-semibold">Currency</label>
                <select className="input" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}>
                  <option value="aud">AUD</option>
                  <option value="usd">USD</option>
                  <option value="eur">EUR</option>
                </select>
              </div>
            </>
          )}

          <div>
            <label className="block mb-1 text-xs font-semibold">Max uses</label>
            <input type="number" min="1" className="input" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: parseInt(e.target.value, 10) })} />
          </div>
          <div>
            <label className="block mb-1 text-xs font-semibold">Expires at (optional)</label>
            <input type="date" className="input" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} />
          </div>
          <div>
            <label className="block mb-1 text-xs font-semibold">Note (perso)</label>
            <input className="input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Launch offer" />
          </div>
          <button type="submit" className="btn-primary w-full" disabled={creating} data-testid="promo-create-btn">
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Ticket className="w-4 h-4" /> Create code</>}
          </button>
        </form>
      </div>

      {/* List */}
      <div className="bg-white border border-[#EAE5DB] rounded-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-[#EAE5DB] font-heading font-bold text-[#292524]">Codes ({items.length})</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#FAF6F0] text-left">
                <th className="px-4 py-3 font-semibold">Code</th>
                <th className="px-4 py-3 font-semibold">Effect</th>
                <th className="px-4 py-3 font-semibold">Uses</th>
                <th className="px-4 py-3 font-semibold">Note</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <React.Fragment key={p.code}>
                  <tr className={`border-b border-[#F2EBE1] ${p.disabled ? "opacity-50" : ""}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <code className="font-mono font-bold text-[#292524]">{p.code}</code>
                        <button onClick={() => { navigator.clipboard.writeText(p.code); }} title="Copy">
                          <Copy className="w-3.5 h-3.5 text-[#78716C]" />
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs">{describePromo(p)}</td>
                    <td className="px-4 py-3 text-xs">{p.uses_count}/{p.max_uses}</td>
                    <td className="px-4 py-3 text-xs text-[#78716C]">{p.note || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => showRedemptions(p.code)} className="text-xs text-[#2A5C5A] hover:underline mr-3">View</button>
                      <button onClick={() => toggle(p.code)} className="text-xs text-[#D9423E] hover:underline">
                        {p.disabled ? "Enable" : "Disable"}
                      </button>
                    </td>
                  </tr>
                  {expandedCode === p.code && (
                    <tr>
                      <td colSpan={5} className="px-4 py-3 bg-[#FAF6F0]">
                        <div className="text-xs font-semibold mb-2">Redeemed by:</div>
                        {(redemptions[p.code] || []).map((r) => (
                          <div key={r.user_id + r.redeemed_at} className="text-xs text-[#57534E] py-1">
                            • {r.email} · {(r.redeemed_at || "").slice(0, 16).replace("T", " ")}
                          </div>
                        ))}
                        {(redemptions[p.code] || []).length === 0 && <div className="text-xs text-[#78716C]">No redemptions yet (or Stripe-side redemption — check Stripe dashboard).</div>}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={5} className="text-center py-10 text-[#78716C]">No codes yet — create your first one!</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ActivityTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { authFetch("/admin/activity").then((r) => setItems(r.items || [])); }, []);
  const sevColor = { high: "#D9423E", medium: "#E5A93D", low: "#78716C", info: "#3B8268" };
  return (
    <div className="bg-white border border-[#EAE5DB] rounded-2xl overflow-hidden">
      <div className="px-5 py-3 border-b border-[#EAE5DB] font-heading font-bold text-[#292524]">Recent activity ({items.length})</div>
      <div className="divide-y divide-[#F2EBE1] max-h-[600px] overflow-y-auto">
        {items.map((ev, i) => (
          <div key={i} className="px-5 py-3 flex items-start gap-3">
            <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: sevColor[ev.severity] || "#78716C" }} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-[#292524]">{ev.type}</div>
              <div className="text-xs text-[#78716C]">{JSON.stringify(ev.details || {})}</div>
            </div>
            <div className="text-xs text-[#78716C] whitespace-nowrap">{(ev.created_at || "").slice(0, 16).replace("T", " ")}</div>
          </div>
        ))}
        {items.length === 0 && <div className="text-center py-10 text-[#78716C]">No activity yet.</div>}
      </div>
    </div>
  );
}

function SecurityTab() {
  const [data, setData] = useState(null);
  useEffect(() => { authFetch("/admin/security").then(setData); }, []);
  if (!data) return <div className="text-[#78716C]">Loading…</div>;
  return (
    <div>
      <div className="grid grid-cols-2 gap-4 mb-6">
        <StatCard icon={ShieldAlert} label="High (24h)" value={data.alerts_24h_high} tint="#D9423E" />
        <StatCard icon={ShieldAlert} label="Medium (24h)" value={data.alerts_24h_medium} tint="#E5A93D" />
      </div>
      <div className="bg-white border border-[#EAE5DB] rounded-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-[#EAE5DB] font-heading font-bold text-[#292524]">Recent alerts</div>
        <div className="divide-y divide-[#F2EBE1] max-h-[500px] overflow-y-auto">
          {data.recent.map((ev, i) => (
            <div key={i} className="px-5 py-3 flex items-start gap-3">
              <div className={`text-xs font-bold ${ev.severity === "high" ? "text-[#D9423E]" : "text-[#E5A93D]"}`}>
                {ev.severity.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-[#292524]">{ev.type}</div>
                <div className="text-xs text-[#78716C]">IP: {ev.ip || "—"} · {JSON.stringify(ev.details || {})}</div>
              </div>
              <div className="text-xs text-[#78716C]">{(ev.created_at || "").slice(0, 16).replace("T", " ")}</div>
            </div>
          ))}
          {data.recent.length === 0 && <div className="text-center py-10 text-[#78716C]">No alerts. All good ✅</div>}
        </div>
      </div>
    </div>
  );
}

function SupportTicketsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await authFetch("/admin/support-tickets");
      setItems(r.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="bg-white border border-[#EAE5DB] rounded-2xl overflow-hidden">
      <div className="px-5 py-3 border-b border-[#EAE5DB] flex items-center justify-between">
        <div className="font-heading font-bold text-[#292524]">Support tickets ({items.length})</div>
        <button className="btn-outline !py-1.5 !px-3 text-xs" onClick={load} disabled={loading}>
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : "Refresh"}
        </button>
      </div>
      <div className="divide-y divide-[#F2EBE1] max-h-[600px] overflow-y-auto">
        {items.map((tk) => (
          <div key={tk.ticket_id} className="px-5 py-4" data-testid={`support-ticket-${tk.ticket_id}`}>
            <div className="flex items-start justify-between gap-3 cursor-pointer" onClick={() => setExpanded(expanded === tk.ticket_id ? null : tk.ticket_id)}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${tk.status === "open" ? "bg-[#E5A93D]/15 text-[#9B7A1E]" : "bg-[#3B8268]/15 text-[#3B8268]"}`}>
                    {tk.status.toUpperCase()}
                  </span>
                  <span className="text-xs text-[#78716C]">{tk.email}</span>
                </div>
                <div className="font-semibold text-[#292524] text-sm truncate">{tk.subject || "(No subject)"}</div>
                <div className="text-xs text-[#78716C] mt-0.5 truncate">{tk.message}</div>
              </div>
              <div className="text-xs text-[#78716C] whitespace-nowrap flex items-center gap-2">
                <Mail className="w-3.5 h-3.5" />
                {(tk.created_at || "").slice(0, 16).replace("T", " ")}
              </div>
            </div>
            {expanded === tk.ticket_id && (
              <div className="mt-3 pl-3 border-l-2 border-[#D95D39]/40 text-sm text-[#292524] whitespace-pre-wrap bg-[#FAF6F0] p-3 rounded-lg">
                {tk.message}
                <div className="mt-2 text-xs text-[#78716C]">
                  From: {tk.name || "—"} · {tk.email} · Lang: {tk.lang}
                </div>
              </div>
            )}
          </div>
        ))}
        {items.length === 0 && <div className="text-center py-10 text-[#78716C]">No tickets yet.</div>}
      </div>
    </div>
  );
}

// ============ Main ============

export default function AdminDashboard({ user, onLogout, onExit }) {
  const [tab, setTab] = useState("overview");

  return (
    <div className="min-h-screen bg-[#FAF6F0] flex flex-col md:flex-row" data-testid="admin-dashboard">
      {/* Sidebar (desktop) / Topbar (mobile) */}
      <aside className="md:w-60 w-full bg-[#292524] text-white flex md:flex-col flex-row md:flex-shrink-0 sticky md:relative top-0 z-40">
        <div className="p-4 md:p-5 md:border-b md:border-r-0 border-r border-white/10 flex-shrink-0">
          <div className="font-heading font-extrabold text-lg">AussieCV</div>
          <div className="hidden md:block text-xs text-white/60">Admin console</div>
        </div>
        <nav className="flex-1 md:py-4 py-2 flex md:flex-col flex-row md:overflow-visible overflow-x-auto scrollbar-hide">
          {TABS.map((tb) => {
            const Icon = tb.icon;
            const active = tab === tb.key;
            return (
              <button
                key={tb.key}
                onClick={() => setTab(tb.key)}
                className={`md:w-full flex-shrink-0 flex items-center gap-2 md:gap-3 md:px-5 px-3 md:py-2.5 py-2 text-xs md:text-sm font-semibold transition-colors whitespace-nowrap ${active ? "bg-[#D95D39] text-white" : "text-white/70 hover:text-white hover:bg-white/5"}`}
                data-testid={`admin-tab-${tb.key}`}
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                {tb.label}
              </button>
            );
          })}
        </nav>
        <div className="hidden md:block p-4 border-t border-white/10">
          <div className="flex items-center gap-2 mb-3">
            {user?.picture && <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />}
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate">{user?.name}</div>
              <div className="text-xs text-white/60 truncate">{user?.email}</div>
            </div>
          </div>
          <button onClick={onExit} className="w-full text-xs text-white/70 hover:text-white flex items-center gap-2 py-1">
            ← Back to site
          </button>
          <button onClick={onLogout} className="w-full text-xs text-white/70 hover:text-white flex items-center gap-2 py-1">
            <LogOut className="w-3.5 h-3.5" /> Log out
          </button>
        </div>
        {/* Mobile exit btn */}
        <button
          onClick={onExit}
          className="md:hidden flex-shrink-0 px-3 py-2 text-xs text-white/70 hover:text-white border-l border-white/10 flex items-center"
          aria-label="Back to site"
          title="Back to site"
        >
          ← Site
        </button>
      </aside>

      {/* Main */}
      <main className="flex-1 p-4 md:p-6 lg:p-10 overflow-x-auto">
        <div className="max-w-7xl">
          <h1 className="font-heading font-bold text-xl md:text-2xl text-[#292524] mb-4 md:mb-6">
            {TABS.find((t) => t.key === tab)?.label}
          </h1>
          {tab === "overview" && <OverviewTab />}
          {tab === "users" && <UsersTab />}
          {tab === "promos" && <PromosTab />}
          {tab === "leads" && <GuideLeadsTab />}
          {tab === "support" && <SupportTicketsTab />}
          {tab === "activity" && <ActivityTab />}
          {tab === "security" && <SecurityTab />}
        </div>
      </main>
    </div>
  );
}
